import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './DeleteConfirmationModal.css';

export interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Modal delete confirmation for the Display module, matching Product Catalog's DeleteConfirmationModal contract. */
export function DeleteConfirmationModal({
  open, onClose, onConfirm, title, description, confirmLabel = 'Delete', cancelLabel = 'Cancel',
}: DeleteConfirmationModalProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div onClick={(e) => e.stopPropagation()} className="delete-modal-backdrop-wrap">
      <div onClick={(e) => { e.stopPropagation(); onClose(); }} className="delete-modal-overlay" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="delete-modal-dialog"
      >
        <div className="delete-modal-content">
          <h2 id={titleId} className="delete-modal-title">
            {title}
          </h2>
          <div className="delete-modal-description">
            {description}
          </div>
        </div>
        <div className="delete-modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="delete-modal-cancel-btn"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="delete-modal-confirm-btn"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
}

/** `Delete 'Name'?` — quoted title matching Product Catalog's deleteConfirmTitle() convention. */
export function deleteConfirmTitle(name: string, verb = 'Delete'): string {
  const n = name.trim() || 'this item';
  return `${verb} ‘${n}’?`;
}
